import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadMain,
  MastheadToggle,
  MenuToggle,
  MenuToggleElement,
  Nav,
  NavExpandable,
  NavItem,
  NavList,
  Page,
  PageSidebar,
  PageSidebarBody,
  Select,
  SelectOption,
  SkipToContent,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { IAppRoute, IAppRouteGroup, acmOpenShiftRoutes, ansibleRoutes, edgeManagerRoutes, redHatManagerRoutes } from '@app/routes';
import { BarsIcon, BellIcon, CaretDownIcon, CodeBranchIcon, CogIcon, DesktopIcon, MobileAltIcon, MoonIcon, PlusIcon, SunIcon, ThIcon, TimesIcon } from '@patternfly/react-icons';
import redHatLogo from '@app/bgimages/red-hat-logo.svg';
import { useTheme } from '@app/utils/theme';
import { MobileViewContext } from '@app/AppLayout/MobileViewContext';

interface IAppLayout {
  children: React.ReactNode;
}

type ViewportMode = 'auto' | 'mobile' | 'desktop';
const VIEWPORT_STORAGE_KEY = 'rhem-viewport-mode';

const readStoredViewportMode = (): ViewportMode => {
  try {
    const stored = sessionStorage.getItem(VIEWPORT_STORAGE_KEY);
    if (stored === 'mobile' || stored === 'desktop' || stored === 'auto') {
      return stored;
    }
  } catch {
    // sessionStorage can be unavailable
  }
  return 'auto';
};

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
  const { isDark, toggleColorMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = React.useState(false);
  const [selectedUserAction, setSelectedUserAction] = React.useState('Demo User');
  const [isBrandDropdownOpen, setIsBrandDropdownOpen] = React.useState(false);
  const [selectedFleet] = React.useState('Fleet Management');
  const [viewportMode, setViewportMode] = React.useState<ViewportMode>(readStoredViewportMode);
  const [isNarrowViewport, setIsNarrowViewport] = React.useState(() => window.innerWidth < 768);
  const isMobileView = viewportMode === 'mobile' || (viewportMode === 'auto' && isNarrowViewport);
  const isForcedMobilePreview = viewportMode === 'mobile' && !isNarrowViewport;

  const persistViewportMode = (mode: ViewportMode) => {
    setViewportMode(mode);
    try {
      sessionStorage.setItem(VIEWPORT_STORAGE_KEY, mode);
    } catch {
      // sessionStorage can be unavailable
    }
  };

  const getFleetIcon = (fleet: string) => {
    if (fleet === 'Administrator') {
      return <CogIcon style={{ fontSize: 'var(--pf-t--global--font--size--md)' }} />;
    }
    return <CodeBranchIcon style={{ fontSize: 'var(--pf-t--global--font--size--md)' }} />;
  };
  const location = useLocation();
  const navigate = useNavigate();
  const isACMOpenShiftPage = location.pathname.startsWith('/acm-openshift');
  const isAnsiblePage = location.pathname.startsWith('/ansible');
  const isRedHatManagerPage = location.pathname.startsWith('/red-hat-manager');

  const getCurrentApp = (): string => {
    if (isACMOpenShiftPage) return 'Open Shift (ACM) 2.15';
    if (isAnsiblePage) return 'Ansible Automation';
    return 'Edge Management';
  };

  const currentApp = getCurrentApp();
  const useAcmShell =
    currentApp === 'Open Shift (ACM) 2.15' ||
    currentApp === 'Edge Management' ||
    currentApp === 'Ansible Automation';

  // Use appropriate routes based on current app
  const currentRoutes = isACMOpenShiftPage
    ? acmOpenShiftRoutes
    : isAnsiblePage
      ? ansibleRoutes
      : isRedHatManagerPage
        ? redHatManagerRoutes
        : edgeManagerRoutes;

  // Handle responsive sidebar behavior
  React.useEffect(() => {
    const handleResize = () => {
      setIsNarrowViewport(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    setSidebarOpen(!isMobileView);
  }, [isMobileView]);

  React.useEffect(() => {
    if (!isMobileView || !sidebarOpen) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobileView, sidebarOpen]);

  // Fix dropdown positioning when it opens
  React.useEffect(() => {
    if (!isUserDropdownOpen && !isBrandDropdownOpen) {
      return;
    }

    const fixDropdownPosition = () => {
      // Find all dropdown menus - try multiple selectors
      const dropdownMenus = document.querySelectorAll('.pf-v6-c-select__menu, [class*="select__menu"], .pf-v6-c-menu') as NodeListOf<HTMLElement>;
      
      dropdownMenus.forEach((dropdownMenu) => {
        if (!dropdownMenu) return;
        
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Find the toggle button - check for both user and brand dropdowns
        let toggleButton: HTMLElement | null = null;
        if (isBrandDropdownOpen) {
          // Find brand dropdown toggle
          toggleButton = document.querySelector('.pf-v6-c-masthead .pf-v6-c-menu-toggle[aria-expanded="true"]') as HTMLElement;
        } else if (isUserDropdownOpen) {
          // Find user dropdown toggle
          toggleButton = document.querySelector('[aria-label*="Demo User"], .pf-v6-c-toolbar .pf-v6-c-menu-toggle[aria-expanded="true"]') as HTMLElement;
        }
        
        let toggleRect: DOMRect | null = null;
        if (toggleButton) {
          toggleRect = toggleButton.getBoundingClientRect();
        }
        
        const dropdownWidth = Math.min(250, viewportWidth - 32);
        
        if (isBrandDropdownOpen && toggleRect) {
          // Position brand dropdown below the toggle
          dropdownMenu.style.cssText = `
            position: fixed !important;
            left: ${toggleRect.left}px !important;
            top: ${toggleRect.bottom + 4}px !important;
            transform: none !important;
            max-width: ${dropdownWidth}px !important;
            max-height: ${Math.min(300, viewportHeight - 100)}px !important;
            overflow-y: auto !important;
            z-index: 9999 !important;
            width: auto !important;
            min-width: 200px !important;
          `;
        } else {
          // Position user dropdown from right edge
        dropdownMenu.style.cssText = `
          position: fixed !important;
          left: auto !important;
          right: 16px !important;
          ${toggleRect ? `top: ${toggleRect.bottom + 4}px !important;` : ''}
          transform: none !important;
          max-width: ${dropdownWidth}px !important;
          max-height: ${Math.min(300, viewportHeight - 100)}px !important;
          overflow-y: auto !important;
          z-index: 9999 !important;
          width: auto !important;
        `;
        }
        
        // If dropdown would go below viewport and we have toggle position, position it above
        if (toggleRect && toggleRect.bottom + 200 > viewportHeight && toggleRect.top > 200) {
          dropdownMenu.style.top = `${toggleRect.top - 200}px !important`;
        }
      });
    };

    // Use requestAnimationFrame for smoother updates
    const rafId = requestAnimationFrame(() => {
      fixDropdownPosition();
      // Also try after a small delay
      setTimeout(fixDropdownPosition, 0);
      setTimeout(fixDropdownPosition, 10);
      setTimeout(fixDropdownPosition, 50);
    });

    // Use MutationObserver to catch when dropdown is added to DOM
    const observer = new MutationObserver(() => {
      requestAnimationFrame(fixDropdownPosition);
    });

    // Observe body for added nodes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    
    // Fix on window resize and scroll
    window.addEventListener('resize', fixDropdownPosition);
    window.addEventListener('scroll', fixDropdownPosition, true);
    
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener('resize', fixDropdownPosition);
      window.removeEventListener('scroll', fixDropdownPosition, true);
    };
  }, [isUserDropdownOpen, isBrandDropdownOpen]);

  // Update expanded groups when route changes or when switching apps
  React.useEffect(() => {
    const routesToCheck = isACMOpenShiftPage
      ? acmOpenShiftRoutes
      : isAnsiblePage
        ? ansibleRoutes
        : isRedHatManagerPage
          ? redHatManagerRoutes
          : edgeManagerRoutes;
    const newExpanded: { [key: string]: boolean } = {};
    routesToCheck.forEach((route) => {
      if ('routes' in route) {
        const group = route as IAppRouteGroup;
        const isActive = group.routes.some((r) => r.path === location.pathname);
        if (isActive) {
          newExpanded[group.label] = true;
        }
      }
    });
    if (isACMOpenShiftPage) {
      newExpanded['Edge Management'] = true;
    }
    if (isAnsiblePage) {
      newExpanded['Edge Management'] = true;
    }
    setExpandedGroups(newExpanded);
  }, [location.pathname, isACMOpenShiftPage, isAnsiblePage, isRedHatManagerPage]);

  const onUserDropdownToggle = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const onUserDropdownSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    if (value === 'toggle-theme') {
      toggleColorMode();
      setIsUserDropdownOpen(false);
      return;
    }
    if (value === 'toggle-viewport') {
      persistViewportMode(isMobileView ? 'desktop' : 'mobile');
      setIsUserDropdownOpen(false);
      return;
    }
    setSelectedUserAction(value as string);
    setIsUserDropdownOpen(false);
  };

  const onBrandDropdownToggle = () => {
    setIsBrandDropdownOpen(!isBrandDropdownOpen);
  };

  const onBrandDropdownSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setIsBrandDropdownOpen(false);
    // Handle navigation based on selection
    if (value === 'Edge Management') {
      navigate('/red-hat-manager/overview');
    } else if (value === 'Open Shift (ACM) 2.15') {
      navigate('/acm-openshift/edge-management/overview');
    } else if (value === 'Ansible Automation') {
      navigate('/ansible/edge-management/overview');
    }
  };

  const onNavToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const userToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onUserDropdownToggle}
      isExpanded={isUserDropdownOpen}
      variant="plain"
      className={useAcmShell ? 'acm-masthead__account-toggle' : undefined}
    >
      {isACMOpenShiftPage ? 'kube:admin' : isMobileView ? 'User' : 'Demo User'}
    </MenuToggle>
  );

  const brandToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onBrandDropdownToggle}
      isExpanded={isBrandDropdownOpen}
      variant="plain"
      aria-label="Application context selector"
      className={`app-brand-switcher${useAcmShell ? ' acm-masthead__menu-toggle' : ''}`}
      style={{
        padding: 0,
        color: 'var(--pf-t--global--text--color--regular)',
        fontSize: 'var(--pf-t--global--font--size--body--default)',
        fontWeight: 'var(--pf-t--global--font--weight--body--default)',
        fontFamily: 'var(--pf-t--global--font--family--body)',
        lineHeight: '1',
        minWidth: 'auto',
        width: 'auto',
        maxWidth: 'none',
        whiteSpace: 'nowrap',
        overflow: 'visible',
        textOverflow: 'clip',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--pf-t--global--spacer--sm)', whiteSpace: 'nowrap', overflow: 'visible' }}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>{currentApp}</span>
        <CaretDownIcon style={{ fontSize: 'var(--pf-t--global--font--size--sm)', flexShrink: 0 }} />
      </span>
    </MenuToggle>
  );

  const headerToolbar = (
    <Toolbar>
      <ToolbarContent>
        <ToolbarGroup align={{ default: 'alignEnd' }}>
          {isACMOpenShiftPage && !isMobileView && (
            <>
              <ToolbarItem>
                <Button
                  variant="plain"
                  aria-label="Applications"
                  icon={<ThIcon />}
                />
              </ToolbarItem>
              <ToolbarItem>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Button
                    variant="plain"
                    aria-label="Notifications"
                    icon={<BellIcon />}
                  />
                  <Badge style={{ position: 'absolute', top: '-4px', right: '-4px' }}>6</Badge>
                </div>
              </ToolbarItem>
              <ToolbarItem>
                <Button
                  variant="plain"
                  aria-label="Create"
                  icon={<PlusIcon />}
                />
              </ToolbarItem>
            </>
          )}
          {!isMobileView && (
            <>
              <ToolbarItem>
                <Button
                  variant="plain"
                  className="app-viewport-toggle"
                  aria-label="Switch to mobile view"
                  title="Switch to mobile view"
                  icon={<MobileAltIcon />}
                  onClick={() => persistViewportMode('mobile')}
                />
              </ToolbarItem>
              <ToolbarItem>
                <Button
                  variant="plain"
                  className="app-theme-toggle"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  icon={isDark ? <SunIcon /> : <MoonIcon />}
                  onClick={toggleColorMode}
                />
              </ToolbarItem>
            </>
          )}
          <ToolbarItem>
            <Select
              toggle={userToggle}
              onSelect={onUserDropdownSelect}
              selected={isACMOpenShiftPage ? 'kube:admin' : selectedUserAction}
              isOpen={isUserDropdownOpen}
              onOpenChange={(isOpen) => setIsUserDropdownOpen(isOpen)}
              popperProps={{
                appendTo: () => document.body,
                enableFlip: true,
                  }}
                >
              {isACMOpenShiftPage ? (
                <>
                  <SelectOption value="kube:admin">kube:admin</SelectOption>
                  <SelectOption value="User Preferences">User Preferences</SelectOption>
                  {isMobileView && (
                    <>
                      <SelectOption value="toggle-theme">
                        {isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                      </SelectOption>
                      <SelectOption value="toggle-viewport">
                        Switch to desktop view
                      </SelectOption>
                    </>
                  )}
                  <SelectOption value="Logout">Logout</SelectOption>
                </>
              ) : (
                <>
              <SelectOption value="User Preferences">User Preferences</SelectOption>
              {isMobileView && (
                <>
                  <SelectOption value="toggle-theme">
                    {isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  </SelectOption>
                  <SelectOption value="toggle-viewport">
                    Switch to desktop view
                  </SelectOption>
                </>
              )}
              <SelectOption value="Logout">Logout</SelectOption>
                </>
              )}
            </Select>
          </ToolbarItem>
        </ToolbarGroup>
      </ToolbarContent>
    </Toolbar>
  );

  const masthead = (
    <Masthead className={isMobileView ? 'app-masthead app-masthead--mobile' : 'app-masthead'}>
      <MastheadMain>
        <MastheadToggle>
          <Button
            icon={<BarsIcon />}
            variant="plain"
            onClick={onNavToggle}
            aria-label="Global navigation"
            aria-expanded={sidebarOpen}
          />
        </MastheadToggle>
        <MastheadBrand>
          <div className="app-brand">
            <img className="app-brand__logo" src={redHatLogo} alt="Red Hat" />
            <Select
              className="app-brand-switcher"
              toggle={brandToggle}
              onSelect={onBrandDropdownSelect}
              selected={currentApp}
              isOpen={isBrandDropdownOpen}
              onOpenChange={(isOpen) => setIsBrandDropdownOpen(isOpen)}
              popperProps={{
                appendTo: () => document.body,
                enableFlip: true,
              }}
              style={{ zIndex: 1000 }}
            >
              <SelectOption value="Edge Management" isDisabled={currentApp === 'Edge Management'}>
                Edge Management
              </SelectOption>
              <SelectOption value="Open Shift (ACM) 2.15" isDisabled={currentApp === 'Open Shift (ACM) 2.15'}>
                Open Shift (ACM) 2.15
              </SelectOption>
              <SelectOption
                value="Ansible Automation"
                isDisabled={currentApp === 'Ansible Automation'}
              >
                Ansible Automation
              </SelectOption>
            </Select>
          </div>
        </MastheadBrand>
      </MastheadMain>
      <MastheadContent>
        {headerToolbar}
      </MastheadContent>
    </Masthead>
  );

  const [expandedGroups, setExpandedGroups] = React.useState<{ [key: string]: boolean }>(() => {
    const initialExpanded: { [key: string]: boolean } = {};
    const isACM = location.pathname.startsWith('/acm-openshift');
    const isAnsible = location.pathname.startsWith('/ansible');
    const isRHM = location.pathname.startsWith('/red-hat-manager');
    const routesToCheck = isACM
      ? acmOpenShiftRoutes
      : isAnsible
        ? ansibleRoutes
        : isRHM
          ? redHatManagerRoutes
          : edgeManagerRoutes;
    routesToCheck.forEach((route) => {
      if ('routes' in route) {
        const group = route as IAppRouteGroup;
        const isActive = group.routes.some((r) => r.path === location.pathname);
        if (isActive) {
          initialExpanded[group.label] = true;
        }
      }
    });
    if (isACM) {
      initialExpanded['Edge Management'] = true;
    }
    if (isAnsible) {
      initialExpanded['Edge Management'] = true;
    }
    return initialExpanded;
  });

  const onGroupToggle = (groupId: string, isExpanded: boolean) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: isExpanded,
    }));
  };

  const renderNavItem = (route: IAppRoute, index: number, groupId?: string) => {
    const isActive = route.path === location.pathname;
    const isEdgeManagementLink =
      groupId === 'Edge Management' && Boolean(route.label);
    const isStaticNavItem =
      (isACMOpenShiftPage && Boolean(groupId) && !isEdgeManagementLink) ||
      (isAnsiblePage && !isEdgeManagementLink);

    if (isStaticNavItem) {
      return (
        <NavItem
          key={`${route.label}-${index}`}
          itemId={route.path}
          groupId={groupId}
          isActive={false}
          className="acm-nav-subitem--static"
          preventDefault
        >
          {route.label}
        </NavItem>
      );
    }

    return (
      <NavItem
        key={`${route.label}-${index}`}
        itemId={route.path}
        groupId={groupId}
        isActive={isActive}
        to={route.path}
        preventDefault
        onClick={(_event, _itemId, _groupId, to) => {
          navigate(to);
          if (isMobileView) {
            setSidebarOpen(false);
          }
        }}
      >
        {route.isDisabled ? (
          <span
            aria-disabled="true"
            style={{
              color: 'var(--pf-t--global--text--color--disabled)',
              cursor: 'not-allowed',
              display: 'block',
              pointerEvents: 'none',
            }}
          >
            {route.label}
          </span>
        ) : (
          route.label
        )}
      </NavItem>
    );
  };

  const renderNavGroup = (group: IAppRouteGroup, groupIndex: number) => {
    const isExpanded = expandedGroups[group.label] || false;
    const navRoutes = group.routes.filter((route) => Boolean(route.label));

    return (
      <NavExpandable
        key={`${group.label}-${groupIndex}`}
        id={`${group.label}-${groupIndex}`}
        groupId={group.label}
        title={group.label}
        isExpanded={isExpanded}
        isActive={false}
        className="acm-nav-group"
        onExpand={(_event, expanded) => onGroupToggle(group.label, expanded)}
      >
        {navRoutes.map((route, idx) => renderNavItem(route, idx, group.label))}
      </NavExpandable>
    );
  };

  const Navigation = (
    <Nav id="nav-primary-simple" aria-label="Primary navigation">
      {/* Fleet Management dropdown - Only for ACM OpenShift */}
      {isACMOpenShiftPage && (
        <div className="acm-fleet-select">
          <div role="presentation" className="acm-fleet-select__toggle">
            <span className="acm-fleet-select__label">
              {getFleetIcon(selectedFleet)}
              <span>{selectedFleet}</span>
            </span>
            <CaretDownIcon aria-hidden className="acm-fleet-select__caret" />
          </div>
        </div>
      )}
      <NavList>
        {currentRoutes.map((route, idx) => {
          if (!route.label) return null;
          if ('routes' in route) {
            return renderNavGroup(route as IAppRouteGroup, idx);
          }
          return renderNavItem(route as IAppRoute, idx);
        })}
      </NavList>
    </Nav>
  );

  const Sidebar = (
    <PageSidebar className={isMobileView ? 'app-mobile-nav' : undefined} isSidebarOpen={sidebarOpen}>
      <PageSidebarBody>
        {isMobileView && (
          <div className="app-mobile-nav__header">
            <Button
              icon={<TimesIcon />}
              variant="plain"
              aria-label="Close navigation"
              onClick={() => setSidebarOpen(false)}
            />
            <span className="app-mobile-nav__title">Menu</span>
          </div>
        )}
        {Navigation}
      </PageSidebarBody>
    </PageSidebar>
  );

  const pageId = 'primary-app-container';

  // Force identical 22px content inset in light + dark (kill PF main-container chrome margins)
  React.useEffect(() => {
    if (!useAcmShell) {
      return undefined;
    }
    const apply = () => {
      const cont = document.querySelector('.acm-openshift-shell .pf-v6-c-page__main-container') as HTMLElement | null;
      const main = document.getElementById(pageId);
      const sidebar = document.querySelector('.acm-openshift-shell .pf-v6-c-page__sidebar') as HTMLElement | null;
      const zeroBox = (el: HTMLElement | null) => {
        if (!el) {
          return;
        }
        el.style.setProperty('margin', '0', 'important');
        el.style.setProperty('margin-top', '0', 'important');
        el.style.setProperty('margin-right', '0', 'important');
        el.style.setProperty('margin-bottom', '0', 'important');
        el.style.setProperty('margin-left', '0', 'important');
        el.style.setProperty('margin-inline', '0', 'important');
        el.style.setProperty('margin-inline-start', '0', 'important');
        el.style.setProperty('margin-inline-end', '0', 'important');
        el.style.setProperty('padding', '0', 'important');
        el.style.setProperty('padding-inline', '0', 'important');
        el.style.setProperty('padding-block', '0', 'important');
        el.style.setProperty('max-height', 'none', 'important');
        el.style.setProperty('align-self', 'stretch', 'important');
        el.style.setProperty('border', 'none', 'important');
        el.style.setProperty('border-radius', '0', 'important');
      };
      zeroBox(cont);
      zeroBox(main);
      if (sidebar) {
        sidebar.style.setProperty('margin-inline-end', '0', 'important');
        sidebar.style.setProperty('margin-right', '0', 'important');
      }
    };
    apply();
    const raf = window.requestAnimationFrame(apply);
    return () => window.cancelAnimationFrame(raf);
  }, [useAcmShell, location.pathname]);

  const PageSkipToContent = (
    <SkipToContent
      onClick={(event) => {
        event.preventDefault();
        const primaryContentContainer = document.getElementById(pageId);
        primaryContentContainer?.focus();
      }}
      href={`#${pageId}`}
    >
      Skip to Content
    </SkipToContent>
  );

  return (
    <MobileViewContext.Provider value={isMobileView}>
      <Page
        className={[
          useAcmShell ? 'acm-openshift-shell' : '',
          isMobileView ? 'app-is-mobile' : '',
          isForcedMobilePreview ? 'app-page--mobile-preview' : '',
          isMobileView && sidebarOpen ? 'app-nav-open' : '',
        ]
          .filter(Boolean)
          .join(' ') || undefined}
        mainContainerId={pageId}
        masthead={masthead}
        sidebar={sidebarOpen ? Sidebar : undefined}
        skipToContent={PageSkipToContent}
      >
        <div
          className="app-content-pad"
          style={
            useAcmShell
              ? {
                  paddingTop: isMobileView ? 16 : 22,
                  paddingRight: isMobileView ? 24 : 22,
                  paddingBottom: isMobileView ? 16 : 22,
                  paddingLeft: isMobileView ? 24 : 22,
                  margin: 0,
                  boxSizing: 'border-box',
                }
              : undefined
          }
        >
          {children}
        </div>
      </Page>
    </MobileViewContext.Provider>
  );
};

export { AppLayout };
